import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container,
  Box,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Typography,
  Grid,
  Button,
  Autocomplete,
  CircularProgress,
  Card,
  CardContent,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import { Close, RemoveRedEyeOutlined } from "@mui/icons-material";
import { Search, X, ArrowLeft } from "lucide-react";
import { EditIcon } from "../comps/CustomIcons";
import { getPatientByIdApi } from "../services/patient.service";
import { getPatientApi } from "../services/patient.service";
import { useGetApi } from "../hooks/useGetApi";
import { getMedicinesApi } from "../services/item.service";
import { toast } from "react-toastify";
import { addPharmacyOrderApi } from "../services/medicine-tracking.service";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import Loader from "../components/Loader/loader";

/* @refresh reset */

const EXCLUDE_LABEL = [
  {
    id: 1,
    label: "Sl. No.",
    value: "slNo",
  },
  {
    id: 2,
    label: "Item Code",
    value: "itemCode",
  },
  {
    id: 3,
    label: "Medicine Name",
    value: "medicineName",
  },
  {
    id: 4,
    label: "Current Stock",
    value: "currentStock",
  },
  {
    id: 5,
    label: "Quantity",
    value: "quantity",
  },
  {
    id: 6,
    label: "",
    value: "",
  },
];

const PharmacyOutsider = () => {
  const { patientId } = useParams();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showPatientSearch, setShowPatientSearch] = useState(!patientId);
  const [patientSearch, setPatientSearch] = useState("");
  const [patientSearchBy, setPatientSearchBy] = useState("UHID");
  const [isPatientSearchFocused, setIsPatientSearchFocused] = useState(false);
  const patientSearchContainerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        patientSearchContainerRef.current &&
        !patientSearchContainerRef.current.contains(event.target)
      ) {
        setIsPatientSearchFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const initialRowData = {
    itemCode: "",
    medicineName: "",
    currentStock: "",
    quantity: "",
    _id: "",
  };

  const [excludedDepartments, setExcludedDepartments] = useState([
    { ...initialRowData },
  ]);

  // Get patient by ID (when patientId is provided)
  const shouldFetchPatient = !!(patientId && patientId !== 'undefined' && patientId !== 'null');
  
  const {
    dataList: allData,
    isLoading,
    isError,
    refetch,
  } = useGetApi({
    apiFunction: getPatientByIdApi,
    params: {
      patientId: patientId,
    },
    skip: !shouldFetchPatient, // Skip when shouldFetchPatient is false
  });

  // Get patients for search (when no patientId is provided)
  const shouldFetchPatientsList = showPatientSearch && !shouldFetchPatient;
  
  const {
    dataList: patientsList,
    isLoading: isPatientsLoading,
    isError: isPatientsError,
  } = useGetApi({
    apiFunction: getPatientApi,
    params: {
      search: patientSearch,
      searchBy: patientSearchBy,
      limit: 50,
      page: 1,
      dateFilter: "all",
      filter: "all",
    },
    dependencies: [patientSearch, patientSearchBy],
    debounceDelay: 500,
    dataKey: "patients",
    countKey: "total",
    skip: !shouldFetchPatientsList,
  });

  const { dataList: itemList, isLoading: isItemsLoading, isError: isItemsError } = useGetApi({
    apiFunction: getMedicinesApi,
    params: { limit: Number.MAX_SAFE_INTEGER, itemType: "Medicine" },
  });

  // Get the medicines array - handle different possible structures; dedupe by _id for stable Autocomplete keys
  const medicinesData = itemList?.medicines || itemList?.data?.medicines || itemList || [];
  const medicineOptions = useMemo(() => {
    const arr = Array.isArray(medicinesData) ? medicinesData : [];
    const byId = new Map();
    let fallback = 0;
    for (const m of arr) {
      const id =
        m?._id != null && m._id !== ""
          ? String(m._id)
          : `__no_id__${fallback++}`;
      if (!byId.has(id)) byId.set(id, m);
    }
    return Array.from(byId.values());
  }, [medicinesData]);

  // Handler for selecting a patient from search results
  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setShowPatientSearch(false);
  };

  // Handler for going back to patient search
  const handleBackToSearch = () => {
    setShowPatientSearch(true);
    setSelectedPatient(null);
    setExcludedDepartments([{ ...initialRowData }]);
  };

  // Get the current patient data (either from API or selected patient)
  const currentPatient = patientId ? allData : selectedPatient;

  // Function to calculate age from date of birth
  const calculateAge = (dob) => {
    if (!dob) return 'N/A';
    const birthDate = new Date(dob);
    const today = new Date();
    const age = Math.floor((today - birthDate) / (1000 * 60 * 60 * 24 * 365.25));
    return age >= 0 ? age : 'N/A';
  };

  const handleAddTableRow = () => {
    setExcludedDepartments((prevState) => [
      ...prevState,
      { ...initialRowData }, // Create a new independent row
    ]);
  };

  const handleRemoveTableRow = (rowIndex) => {
    setExcludedDepartments((prevState) =>
      prevState.filter((_, index) => index !== rowIndex)
    );
  };
  const handleAutocompleteChange = (newValue, rowIndex) => {
    if (newValue) {
      setExcludedDepartments((prevState) =>
        prevState.map((row, index) =>
          index === rowIndex
            ? {
                ...row,
                itemCode: newValue.itemCode,
                medicineName: newValue.itemName,
                currentStock: newValue.totalStock,
                _id: newValue._id,
              }
            : row
        )
      );
    }
  };
  const handleSubmit = async () => {
    if (!excludedDepartments.length) {
      toast.error("Add at least one item and try again!");
      return;
    }
    const hasInvalidQuantity = excludedDepartments.some(
      (row) => Number(row?.quantity) === 0
    );

    if (hasInvalidQuantity) {
      console.log(hasInvalidQuantity);
      toast.error("Remove the item with empty quantity and try again!");
      return;
    }
    const payload = {
      patientId: currentPatient?._id,
      UHID: currentPatient?.UHID || currentPatient?.uhid,
      medicines: excludedDepartments.map((row) => ({
        item: row?._id,
        quantity: row?.quantity,
      })),
    };
    setLoading(true);
    try {
      let response = await addPharmacyOrderApi(payload);
      if (response?.code === 200 || response?.code === 201) {
        setLoading(false);
        toast.success(
          "Pharmacy order created successfully" || response?.message
        );
        // Navigate back to pharmacy page after successful order
        setTimeout(() => {
          navigate('/pharmacy');
        }, 1500); // Wait 1.5 seconds to show the success message
      } else {
        setLoading(false);
        toast.error(response?.message || "Some error occurred.");
      }
    } catch (error) {
      setLoading(false);
      toast.error(error?.message || "Some error occurred.");
    }
  };

  const handleQuantityChange = (event, rowIndex) => {
    let { value } = event.target;
    value = Number(value);

    setExcludedDepartments((prevState) =>
      prevState.map((row, index) => {
        if (index === rowIndex) {
          if (value < 0) {
            toast.error("Quantity cannot be less than 0");
            return { ...row, quantity: 0 };
          }
          if (value > row.currentStock) {
            toast.error("Quantity cannot exceed current stock");
            return { ...row, quantity: 0 };
          }
          return { ...row, quantity: value };
        }
        return row;
      })
    );
  };

  const outsiderPatientRows = Array.isArray(patientsList)
    ? patientsList
    : patientsList?.patients || [];

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box
          sx={{
            width: "100%",
            pr: { xs: "10px", sm: "20px", md: "40px", lg: "60px", xl: "90px" },
            pl: { xs: "10px", sm: "20px", md: "20px" },
            maxWidth: "100vw",
            overflow: "hidden",
            py: "20px",
          }}
        >
          <Box sx={{ marginBottom: "20px" }}>
            <Button
              startIcon={<ArrowLeft />}
              onClick={() => navigate("/pharmacy")}
              sx={{
                color: "#5D6A82",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: "bold",
                padding: 0,
                transition: "none !important",
                marginBottom: "16px",
                display: "flex",
                justifyContent: "flex-start",
                minWidth: 0,
                width: "fit-content",
                maxWidth: "100%",
                mx: 0,
                px: 0,
                "& .MuiButton-startIcon": {
                  marginLeft: 0,
                  marginRight: "8px",
                },
              }}
            >
              Back
            </Button>
            <Typography
              sx={{
                fontFamily: "Open Sans",
                fontWeight: 600,
                fontSize: "20px",
                lineHeight: "29px",
                letterSpacing: "0px",
                color: "#5D6A82",
                marginTop: 0,
                marginBottom: "1rem",
                display: "block",
              }}
            >
              Pharmacy Outsider
            </Typography>
          </Box>

          {showPatientSearch ? (
            <Box>
              <Typography
                variant="h6"
                sx={{ m: 0, mb: 0, color: "#333", fontWeight: 500 }}
              >
                Select Patient for Medicine Order
              </Typography>

              {/* Search row: same Box layout as Patient.jsx (paddingTop / flex / marginBottom) */}
              <Box
                sx={{
                  paddingTop: "20px",
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "20px",
                }}
              >
                <Box
                  ref={patientSearchContainerRef}
                  sx={{
                    display: "flex",
                    backgroundColor: "white",
                    border: isPatientSearchFocused
                      ? "2px solid #0179B0"
                      : "1px solid #e0e0e0",
                    borderRadius: "8px",
                    alignItems: "center",
                    position: "relative",
                    boxShadow: isPatientSearchFocused
                      ? "0 4px 12px rgba(1, 121, 176, 0.15)"
                      : "0 2px 4px rgba(0, 0, 0, 0.05)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                      borderColor: "#c0c0c0",
                    },
                  }}
                >
                  {isPatientSearchFocused && (
                    <select
                      onChange={(e) => setPatientSearchBy(e.target.value)}
                      value={patientSearchBy}
                      style={{
                        border: "none",
                        outline: "none",
                        background: "transparent",
                        padding: "12px 16px",
                        fontSize: "14px",
                        width: `${Math.max(
                          120,
                          (patientSearchBy === "UHID"
                            ? "UHID"
                            : patientSearchBy === "firstName"
                              ? "Patient's First Name"
                              : patientSearchBy === "phone"
                                ? "Contact Number"
                                : "UHID"
                          ).length *
                            9 +
                            30
                        )}px`,
                        transition: "all 0.3s ease",
                        cursor: "pointer",
                        borderRight: "1px solid #e0e0e0",
                        color: "#333",
                        fontWeight: "500",
                        touchAction: "manipulation",
                      }}
                    >
                      <option value="UHID">UHID</option>
                      <option value="firstName">Patient's First Name</option>
                      <option value="phone">Contact Number</option>
                    </select>
                  )}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      padding: "12px 16px",
                      width: "300px",
                      transition: "all 0.3s ease",
                      gap: "10px",
                    }}
                  >
                    <Search
                      size={20}
                      style={{
                        color: isPatientSearchFocused ? "#0179B0" : "#666",
                        transition: "color 0.3s ease",
                        flexShrink: 0,
                      }}
                    />
                    <input
                      type="text"
                      placeholder={`Search by ${
                        patientSearchBy === "UHID"
                          ? "UHID"
                          : patientSearchBy === "firstName"
                            ? "Patient's First Name"
                            : patientSearchBy === "phone"
                              ? "Contact Number"
                              : "UHID"
                      }`}
                      value={patientSearch || ""}
                      onChange={(e) => setPatientSearch(e.target.value)}
                      onFocus={() => setIsPatientSearchFocused(true)}
                      style={{
                        border: "none",
                        outline: "none",
                        background: "transparent",
                        width: "100%",
                        fontSize: "14px",
                        color: "#333",
                        padding: "0",
                        minHeight: "24px",
                        touchAction: "manipulation",
                        WebkitTapHighlightColor: "transparent",
                      }}
                    />
                    {patientSearch ? (
                      <X
                        size={18}
                        style={{
                          cursor: "pointer",
                          color: "#999",
                          flexShrink: 0,
                          padding: "2px",
                          transition: "all 0.2s ease",
                          touchAction: "manipulation",
                        }}
                        onClick={() => setPatientSearch("")}
                        onMouseEnter={(e) => {
                          e.target.style.color = "#0179B0";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = "#999";
                        }}
                      />
                    ) : null}
                  </Box>
                </Box>
              </Box>

              {/* Patients List */}
              {isPatientsLoading ? (
                <Loader />
              ) : isPatientsError ? (
                <ErrorMessage />
              ) : (
                <Grid container spacing={2}>
                  {outsiderPatientRows.map((patient) => (
                    <Grid item xs={12} md={6} lg={4} key={patient._id}>
                      <Card 
                        sx={{ 
                          cursor: "pointer",
                          "&:hover": {
                            boxShadow: 3,
                            backgroundColor: "#f5f5f5"
                          }
                        }}
                        onClick={() => handlePatientSelect(patient)}
                      >
                        <CardContent>
                          <Typography variant="h6" sx={{ fontSize: "1rem", mb: 1 }}>
                            {patient.firstName} {patient.lastName}
                          </Typography>
                          <Typography color="textSecondary" variant="body2">
                            UHID: {patient.UHID || patient.uhid || 'N/A'}
                          </Typography>
                          <Typography color="textSecondary" variant="body2">
                            Phone: {patient.contactNumber || patient.phone || 'N/A'}
                          </Typography>
                          <Typography color="textSecondary" variant="body2">
                            Age: {calculateAge(patient.dob)} | Gender: {patient.gender || 'N/A'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                  {outsiderPatientRows.length === 0 && patientSearch && (
                    <Grid item xs={12}>
                      <Typography color="textSecondary" sx={{ textAlign: "center", py: 4 }}>
                        No patients found matching your search criteria
                      </Typography>
                    </Grid>
                  )}
                  {!patientSearch && (
                    <Grid item xs={12}>
                      <Typography color="textSecondary" sx={{ textAlign: "center", py: 4 }}>
                        Start typing to search for patients
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              )}
            </Box>
          ) : (
            // Medicine Order Form
            <>
              {(patientId ? isLoading : false) ? (
                <Loader />
              ) : (patientId ? isError : false) ? (
                <ErrorMessage />
              ) : (
                <>
                  {/* Patient Information */}
                  <Box
                    sx={{
                      backgroundColor: "rgba(240, 247, 252, 1)",
                      borderRadius: "12px",
                      mb: "30px",
                      p: "20px",
                    }}
                  >
                    <Typography sx={{ mb: "20px", color: "rgba(93, 106, 130, 1)" }}>
                      Patient Information
                    </Typography>
                    <Grid container spacing={2}>
                      {/* contactNumber */}
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          label="UHID"
                          value={currentPatient?.UHID || currentPatient?.uhid || "-"}
                          fullWidth
                          required
                          slotProps={{ input: { readOnly: true } }}
                        />
                      </Grid>
                      {/* uhid */}
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          label="Patient Name"
                          value={`${currentPatient?.firstName || "-"} ${
                            currentPatient?.lastName || "-"
                          }`}
                          fullWidth
                          required
                          slotProps={{ input: { readOnly: true } }}
                        />
                      </Grid>

                      {/* name */}
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          label="Contact Number"
                          value={currentPatient?.contactNo || currentPatient?.contactNumber || currentPatient?.phone || "-"}
                          fullWidth
                          required
                          slotProps={{ input: { readOnly: true } }}
                        />
                      </Grid>
                      {/* doctorName */}
                    </Grid>
                  </Box>

                  <TableContainer component={Paper} sx={{ mb: "30px" }}>
                    <Table>
                      <TableHead
                        sx={{
                          bgcolor: "#EAEAEA",
                        }}
                      >
                        <TableRow>
                          {EXCLUDE_LABEL?.map((option) => (
                            <TableCell
                              key={option?.id}
                              sx={{
                                textAlign: option?.align || "left",
                                color: "#5D6A82",
                              }}
                            >
                              {option?.label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {excludedDepartments?.map((row, rowIndex) => (
                          <TableRow key={rowIndex}>
                            <TableCell>
                              <Typography>{rowIndex + 1}</Typography>
                            </TableCell>
                            <TableCell>
                              <Autocomplete
                                options={medicineOptions}
                                getOptionLabel={(option) => option?.itemCode || ""}
                                isOptionEqualToValue={(a, b) =>
                                  a?._id != null && b?._id != null && a._id === b._id
                                }
                                value={
                                  row?._id
                                    ? medicineOptions.find(
                                        (item) => String(item?._id) === String(row._id)
                                      ) || null
                                    : null
                                }
                                onChange={(_, newValue) =>
                                  handleAutocompleteChange(newValue, rowIndex)
                                }
                                loading={isItemsLoading}
                                noOptionsText={
                                  isItemsLoading ? "Loading medicines..." : 
                                  isItemsError ? "Error loading medicines" :
                                  "No medicines found"
                                }
                                renderOption={(props, option, state) => {
                                  const { key: _k, ...rest } = props;
                                  const optKey =
                                    option?._id != null && option._id !== ""
                                      ? String(option._id)
                                      : `opt-${state.index}`;
                                  return (
                                    <li {...rest} key={optKey}>
                                      {option?.itemCode ?? ""}
                                    </li>
                                  );
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Item Code"
                                    fullWidth
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Autocomplete
                                options={medicineOptions}
                                getOptionLabel={(option) => option?.itemName || ""}
                                isOptionEqualToValue={(a, b) =>
                                  a?._id != null && b?._id != null && a._id === b._id
                                }
                                value={
                                  row?._id
                                    ? medicineOptions.find(
                                        (item) => String(item?._id) === String(row._id)
                                      ) || null
                                    : null
                                }
                                onChange={(_, newValue) =>
                                  handleAutocompleteChange(newValue, rowIndex)
                                }
                                loading={isItemsLoading}
                                noOptionsText={
                                  isItemsLoading ? "Loading medicines..." : 
                                  isItemsError ? "Error loading medicines" :
                                  "No medicines found"
                                }
                                renderOption={(props, option, state) => {
                                  const { key: _k, ...rest } = props;
                                  const optKey =
                                    option?._id != null && option._id !== ""
                                      ? String(option._id)
                                      : `opt-${state.index}`;
                                  return (
                                    <li {...rest} key={optKey}>
                                      {option?.itemName ?? ""}
                                    </li>
                                  );
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Medicine Name"
                                    fullWidth
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography>{row?.currentStock}</Typography>
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="number"
                                label="Quantity"
                                name="quantity"
                                value={row?.quantity || ""}
                                onChange={(e) => handleQuantityChange(e, rowIndex)}
                                required
                                fullWidth
                                sx={{ bgcolor: "white" }}
                              />
                            </TableCell>
                            <TableCell
                              key={`${rowIndex}`}
                              sx={{ color: "#5D6A82" }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <RemoveRedEyeOutlined
                                  sx={{
                                    cursor: "pointer",
                                    fontSize: "18px",
                                  }}
                                />
                                <EditIcon
                                  sx={{
                                    cursor: "pointer",
                                    color: "transparent",
                                    fontSize: "18px",
                                  }}
                                />
                                <Close
                                  sx={{ cursor: "pointer", fontSize: "18px" }}
                                  onClick={() => handleRemoveTableRow(rowIndex)}
                                />
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell
                            colSpan={EXCLUDE_LABEL?.length}
                            sx={{ color: "#92A6CC", cursor: "pointer" }}
                            onClick={handleAddTableRow}
                          >
                            + Add item
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* submit button */}
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Button
                      variant="contained"
                      type="submit"
                      sx={{
                        minWidth: "150px",
                        textTransform: "none",
                      }}
                      onClick={handleSubmit}
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={24} /> : `Submit`}
                    </Button>
                  </Box>
                </>
              )}
            </>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default PharmacyOutsider;
